// Camera for the body map: which transform puts a muscle group in the middle of the stage.
// The SVG fills the stage (viewBox fitted with "meet"); the camera is a CSS transform on its
// wrapper (origin 0 0): screen = (x, y) + scale * point. Identity = the whole body.

/** [x, y, width, height] in viewBox units. */
export type Box = readonly [number, number, number, number];
export interface Camera { x: number; y: number; scale: number }
export interface Stage {
  width: number;
  height: number;
  /** Space covered at the bottom (the group panel); the group is centred above it. */
  insetBottom?: number;
}

export const OVERVIEW: Camera = { x: 0, y: 0, scale: 1 };

export const ZOOM = {
  /** Never zoom further than this (keeps the anatomy readable and the raster sharp). */
  maxScale: 3.6,
  /** Slivers (a group barely visible from this side) zoom as if they were at least this big. */
  minBox: 44,
  /** Breathing room around the group, as a fraction of its size. */
  pad: 0.22,
  durationIn: 380,
  durationOut: 320,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

/** Where the viewBox lands in the stage before any zoom. */
export function fit(viewBox: Box, stage: Stage): { k: number; ox: number; oy: number } {
  const [vx, vy, vw, vh] = viewBox;
  const k = Math.min(stage.width / vw, stage.height / vh);
  return { k, ox: (stage.width - vw * k) / 2 - vx * k, oy: (stage.height - vh * k) / 2 - vy * k };
}

/** Camera that centres `box` in the visible part of the stage, as large as fits (within limits). */
export function cameraFor(box: Box, viewBox: Box, stage: Stage): Camera {
  if (stage.width <= 0 || stage.height <= 0) return OVERVIEW;
  const { k, ox, oy } = fit(viewBox, stage);
  const [bx, by, bw0, bh0] = box;
  const cx = bx + bw0 / 2;
  const cy = by + bh0 / 2;
  const bw = Math.max(bw0, ZOOM.minBox) * (1 + 2 * ZOOM.pad);
  const bh = Math.max(bh0, ZOOM.minBox) * (1 + 2 * ZOOM.pad);
  const visible = Math.max(stage.height - (stage.insetBottom ?? 0), stage.height * 0.3);
  const scale = Math.min(ZOOM.maxScale, Math.max(1, Math.min(stage.width / (bw * k), visible / (bh * k))));
  return { scale, x: stage.width / 2 - scale * (ox + cx * k), y: visible / 2 - scale * (oy + cy * k) };
}

export const cameraCss = (c: Camera) => `translate(${round(c.x)}px, ${round(c.y)}px) scale(${round(c.scale, 4)})`;

const round = (v: number, places = 2) => Math.round(v * 10 ** places) / 10 ** places;

export const sameCamera = (a: Camera, b: Camera) =>
  Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5 && Math.abs(a.scale - b.scale) < 0.001;
